/**
 * Custom (user-built) scenarios.
 *
 * Instructors can capture the current sim state as a named scenario, which is
 * stored in the browser (localStorage) and can be exported/imported as a JSON
 * file to share with other instructors — consistent with the app's offline,
 * no-backend design. Applying a custom scenario just sets the (already-synced)
 * rhythm / vitals / channels / vent, so the monitor never needs the definition.
 */

const LS_KEY = 'sim.customScenarios.v1';

// Vital keys a scenario carries (absolute target values).
export const SCENARIO_VITAL_KEYS = [
  'HR', 'SpO2', 'NIBP_Sys', 'NIBP_Dia', 'ABP_Sys', 'ABP_Dia', 'EtCO2', 'RR', 'Temp', 'CVP',
];

const RHYTHMS = new Set([
  'normal', 'vf', 'asystole', 'cpr_hq', 'cpr_lq', 'vtach', 'tdp', 'afib', 'svt',
  'st_depression', 'st_elevation', 'hyperkalemia', 'hypothermia', 'paced', 'tamponade',
  'vtach_pulseless', 'cpr_mech', 'iabp', 'impella',
]);
const CAPNOS = new Set(['normal', 'rebreathing', 'curare_cleft', 'obstructed']);

// ── Persistence ──────────────────────────────────────────────────────────────
export function loadCustomScenarios() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.map((s) => validateScenario(s).scenario).filter(Boolean);
  } catch {
    return [];
  }
}

export function saveCustomScenarios(list) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch { /* quota / privacy mode — non-fatal */ }
}

// ── Capture the current sim state as a scenario ──────────────────────────────
export function captureScenario(name, state) {
  const v = state.vitals || {};
  const round = (x) => (typeof x === 'number' ? Math.round(x * 10) / 10 : 0);
  const vitals = {};
  SCENARIO_VITAL_KEYS.forEach((k) => {
    const vv = v[k];
    if (vv) vitals[k] = round(vv.target ?? vv.current);
  });
  return {
    key: `custom_${Date.now().toString(36)}`,
    name: String(name || 'Custom Scenario').trim().slice(0, 60),
    description: 'Custom scenario',
    custom: true,
    rhythm: state.rhythm || 'normal',
    capnoMorphology: state.capnoMorphology || 'normal',
    channels: { ...(state.visibleChannels || {}) },
    vent: { ...(state.ventSettings || {}) },
    // Capture airway status explicitly so re-applying the scenario restores the
    // real intubation state (every capture carries a vent block, which would
    // otherwise be read as "intubated" on load).
    intubated: !!state.intubated,
    vitals,
  };
}

// ── Validation / normalisation ───────────────────────────────────────────────
export function validateScenario(obj) {
  if (!obj || typeof obj !== 'object') return { ok: false, error: 'not an object' };
  const name = String(obj.name ?? '').trim();
  if (!name) return { ok: false, error: 'missing required "name" field — not imported' };
  if (!obj.vitals || typeof obj.vitals !== 'object') return { ok: false, error: `"${name}": missing vitals` };

  const vitals = {};
  for (const k of SCENARIO_VITAL_KEYS) {
    const val = obj.vitals[k];
    if (val == null) continue;
    const n = Number(val);
    if (!Number.isFinite(n)) return { ok: false, error: `"${name}": vital ${k} is not a number` };
    vitals[k] = n;
  }
  if (Object.keys(vitals).length === 0) return { ok: false, error: `"${name}": no valid vitals` };

  const rhythm = RHYTHMS.has(obj.rhythm) ? obj.rhythm : 'normal';
  const capnoMorphology = CAPNOS.has(obj.capnoMorphology) ? obj.capnoMorphology : 'normal';

  const scenario = {
    key: typeof obj.key === 'string' && obj.key ? obj.key : `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    name: name.slice(0, 60),
    description: String(obj.description ?? 'Custom scenario').slice(0, 200),
    custom: true,
    rhythm,
    capnoMorphology,
    channels: obj.channels && typeof obj.channels === 'object' ? { ...obj.channels } : undefined,
    vent: obj.vent && typeof obj.vent === 'object' ? { ...obj.vent } : undefined,
    intubated: typeof obj.intubated === 'boolean' ? obj.intubated : undefined,
    interventions: sanitizeInterventions(obj.interventions),
    vitals,
  };
  return { ok: true, scenario };
}

// Per-intervention response map: { interventionKey: { HR:-20, NIBP_Sys:+30, …,
// rhythm?, capnoMorphology? } }. Numeric vital deltas are kept; rhythm/capnogram
// side-effects pass through as strings. Lets a scenario file define how each
// intervention affects it, so "correct" treatments visibly work.
function sanitizeInterventions(map) {
  if (!map || typeof map !== 'object') return undefined;
  const out = {};
  for (const [ivKey, deltas] of Object.entries(map)) {
    if (!deltas || typeof deltas !== 'object') continue;
    const clean = {};
    for (const k of SCENARIO_VITAL_KEYS) {
      const n = Number(deltas[k]);
      if (deltas[k] != null && Number.isFinite(n)) clean[k] = n;
    }
    if (RHYTHMS.has(deltas.rhythm)) clean.rhythm = deltas.rhythm;
    if (CAPNOS.has(deltas.capnoMorphology)) clean.capnoMorphology = deltas.capnoMorphology;
    if (Object.keys(clean).length) out[ivKey] = clean;
  }
  return Object.keys(out).length ? out : undefined;
}

// ── Export / import ──────────────────────────────────────────────────────────
export const EXPORT_FORMAT = 'anesthesia-sim-scenario';

export function buildExport(scenarios, appVersion) {
  return JSON.stringify({
    format: EXPORT_FORMAT,
    version: 1,
    appBuild: appVersion ?? (typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev'),
    exportedAt: new Date().toISOString(),
    scenarios,
  }, null, 2);
}

/**
 * Parse an imported file/text. Accepts an export envelope, a bare array, or a
 * single scenario object. Returns { scenarios: [...valid], errors: [...strings] }.
 */
export function parseImport(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return { scenarios: [], errors: ['File is not valid JSON.'] };
  }
  let raw;
  if (Array.isArray(data)) raw = data;
  else if (data && Array.isArray(data.scenarios)) raw = data.scenarios;
  else if (data && typeof data === 'object') raw = [data];
  else return { scenarios: [], errors: ['Unrecognised file structure.'] };

  const scenarios = [];
  const errors = [];
  raw.forEach((s, i) => {
    const r = validateScenario(s);
    if (r.ok) scenarios.push(r.scenario);
    else errors.push(`Scenario ${i + 1}: ${r.error}`);
  });
  if (scenarios.length === 0 && errors.length === 0) errors.push('No scenarios found in file.');
  return { scenarios, errors };
}
